import React from "react";
import { Box } from "@mui/material";

// Keep every tab-view sidebar aligned with the Companies page.
const TAB_VIEW_SIDEBAR_WIDTH = 392;

/**
 * Reusable page shell:
 * - Breadcrumb
 * - Optional stats section
 * - Optional header section
 * - Main content
 * - Optional right sidebar
 *
 * New slot props let pages compose their own sections while keeping a shared shell.
 * Legacy props are still supported so older usages keep working during the refactor.
 */
export default function ListTableTabsTemplate({
  breadcrumb,

  statsSection,
  headerSection,
  contentSection,
  footerSection,

  // Legacy props kept for compatibility.
  statsSlider,
  showStats = true,
  viewMode = "list",
  listContent,
  tabsContent,

  rightSidebar,

  rootSx,
  shellSx,
  layoutSx,
  leftColumnSx,
  rightSidebarSx,
  statsWrapperSx,
  headerWrapperSx,
  contentWrapperSx,
  footerWrapperSx,
}) {
  const isTabs = viewMode === "tabs";

  const resolvedStatsSection =
    statsSection ?? (showStats && statsSlider ? statsSlider : null);

  const resolvedHeaderSection = headerSection ?? null;

  const resolvedContentSection =
    contentSection ?? (isTabs ? tabsContent : listContent);
  const shellClassName = rightSidebar ? undefined : "main-border-radius";
  const leftColumnClassName = rightSidebar
    ? "tab-content-column main-border-radius"
    : "tab-content-column";
  const resolvedShellSx = rightSidebar
    ? { ...(shellSx || {}), pt: 0 }
    : shellSx;

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, ...rootSx }}>
      {breadcrumb ? <Box sx={{ minWidth: 0 }}>{breadcrumb}</Box> : null}

      <Box
        className={shellClassName}
        sx={{ pb: 2, width: "100%", maxWidth: "100%", minWidth: 0, ...resolvedShellSx }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 4,
            alignItems: "flex-start",
            flexDirection: { xs: "column", md: "row" },
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            ...layoutSx,
          }}
        >
          <Box
            className={leftColumnClassName}
            sx={{
              flex: 1,
              minWidth: 0,
              width: "100%",
              maxWidth: "100%",
              backgroundColor: "#fff",
              overflow: "hidden",
              ...leftColumnSx,
            }}
          >
            {resolvedStatsSection ? (
              <Box sx={{ px: 0, pb: 2, ...statsWrapperSx }}>
                {resolvedStatsSection}
              </Box>
            ) : null}

            {resolvedHeaderSection ? (
              <Box sx={headerWrapperSx}>{resolvedHeaderSection}</Box>
            ) : null}

            {resolvedContentSection ? (
              <Box sx={{ p: 0, width: "100%", maxWidth: "100%", minWidth: 0, ...contentWrapperSx }}>
                {resolvedContentSection}
              </Box>
            ) : null}

            {footerSection ? (
              <Box sx={footerWrapperSx}>{footerSection}</Box>
            ) : null}
          </Box>

          {rightSidebar ? (
            <Box
              sx={{
                width: { xs: "100%", md: TAB_VIEW_SIDEBAR_WIDTH },
                maxWidth: "100%",
                minWidth: 0,
                "& > *": {
                  width: "100%",
                  maxWidth: "100%",
                },
                "& > *:not(:last-child)": {
                  marginBottom: "32px !important",
                },
                "& > *:last-child": {
                  marginBottom: "0 !important",
                },
                ...rightSidebarSx,
              }}
            >
              {rightSidebar}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
